import {TaskTypeEnum} from 'src/api/ProactApiClient';
import type {BulletinViewType} from 'src/app/bulletins/BulletinForm/BulletinForm';
import type {CourseVideoViewType} from 'src/app/course-video/CourseVideoForm';
import type {ItemDefinitionViewType} from 'src/app/item-tracker/ItemDefinitionForm';
import type {RiskAssessmentCodeViewType} from 'src/app/risk-assessment-code/RiskAssessmentCodeForm/RiskAssessmentForm';
import type {SafetyEventViewType} from 'src/app/safety-events/SafetyEventForm';
import {GetUrlSectionFromTaskType} from 'src/app/tasks/taskHelpers';
import type {UserGroupViewType} from 'src/app/user-groups/UserGroupForm';
import type {
  AssetsViewType,
  AuditLogsViewType,
  BulletinDashViewType,
  ClientIndexViewType,
  CourseAssignmentViewType,
  CourseClassroomViewType,
  CourseDefinitionViewType,
  DocumentManagementViewType,
  FormsDashViewType,
  FormViewType,
  InspectionIndexViewType,
  JobHazardAnalysisViewType,
  PersonnelIndexViewType,
  PersonnelViewType,
  ProjectIndexViewType,
  SignOffTabView,
  TasksDashViewType,
  TrainingComplianceTabView,
  TrainingDashViewType,
  TrainingScheduleViewMode,
  VendorIndexViewType,
  ViewCourseSessionRecordViewType,
} from 'src/shared/constants/ViewTypes';

class PathBuilder {
  private ACCOUNTS: string = 'accounts';
  private ACTIONS: string = 'actions';
  private ADMIN: string = 'admin';
  private ANALYTICS: string = 'analytics';
  private ASSETS: string = 'assets';
  private ASSET_TYPES: string = 'assettypes';
  private ASSIGNED_FORMS: string = 'assignedforms';
  private ASSISTANT: string = 'assistant';
  private AUDIT_LOGS: string = 'audit-logs';
  private BACKGROUND_JOBS: string = 'background-jobs';
  private BULLETIN: string = 'bulletins';
  private COMPLIANCE: string = 'compliance';
  private COMPLIANCE_STANDARDS: string = 'compliance-standards';
  private COURSE_BUILDER: string = 'course-builder';
  private COURSE_VIDEO: string = 'course-video';
  private CREATE_ACCOUNT: string = 'create/account';
  private CLIENTS: string = 'clients';
  private CUSTOM_COMPLIANCE_STANDARDS: string = 'custom-compliance-standards';
  private DASHBOARD: string = 'dashboard';
  private DEPARTMENT: string = 'department';
  private DOCUMENTS: string = 'documents';
  private DOCUMENTS_MANAGER: string = 'document-manager';
  private ERROR: string = 'error';
  private ITEM_ASSIGNMENT: string = 'item-assignment';
  private ITEM_DEFINITION: string = 'item-definition';
  private ITEM_TRACKER: string = 'item-tracker';
  private FORGOT_PASSWORD: string = 'forgot-password';
  private FORM_BUILDER: string = 'form-builder';
  private FORMS: string = 'form';
  private INACTIVE_USER: string = 'inactive-user';
  private INSPECTIONS: string = 'inspections';
  private INSTANCES: string = 'instances';
  private INSTANCE_COMPLIANCE_STANDARDS: string =
    'instance-compliance-standards';
  private KNOWLEDGE_BASE: string = 'knowledge-base';
  private LANDING: string = '/';
  private LOGIN: string = 'login';
  private LOGOUT: string = 'logout';
  private NOTIFICATION_RULE: string = 'notification-rule';
  private NOT_FOUND: string = '404';
  private PAGE_NOT_FOUND: string = 'page-not-found';
  private PERFORMANCE: string = 'performance';
  private PERMISSIONS: string = 'permissions';
  private PERMISSION_DENIED: string = 'permission-denied';
  private PERSONNEL: string = 'personnel';
  private RESET_PASSWORD: string = 'password-reset';
  private RISK_ASSESSMENT: string = 'riskassessment';
  private RISK_ASSESSMENT_MISSING: string = 'rac-missing';
  private SAFETY_EVENT: string = 'safetyevent';
  private SECURITY: string = 'security';
  private SIGNUP: string = 'signup';
  private SEND_MAGIC_LINK: string = 'send-magic-link';
  private SYSTEM: string = 'system';
  private TOOLBOX_TALK: string = 'toolboxtalks';
  private TRAINING: string = 'training';
  private UNDER_CONSTRUCTION: string = 'development';
  private JOB_HAZARD_ANALYSIS: string = 'job-hazard-analysis';
  private USER_GROUP: string = 'user-group';
  private MODULE_CUSTOMIZATION: string = 'module-customization';
  private OSHA: string = 'osha';
  private ATTACHMENT_CLASSIFICATION: string = 'attachment-classifications';
  private PROJECT: string = 'projects';
  private VENDORS: string = 'vendors';
  private WORKFLOW: string = 'workflows';
  private SUPER_ADMINS: string = 'super-admins';

  ToAbsoluteUrl(part: string) {
    return `${window.location.origin}${part}`;
  }

  Actions(view: TasksDashViewType): string {
    return `${this.Dashboard()}/${this.ACTIONS}/${view}`;
  }

  ActionView(
    taskId: string | number,
    view: 'content' | 'details' | 'assign' | 'notes' | 'notifications' | ':view'
  ): string {
    return `${this.Actions('manage')}/${taskId}/view/${view}`;
  }

  /** User-facing action view (from My Actions tab) */
  ActionViewUser(
    taskId: string | number,
    view: 'content' | 'details' | 'assign' | 'notes' | 'notifications' | ':view'
  ): string {
    return `${this.Actions('user')}/${taskId}/view/${view}`;
  }

  ActionClone(taskId: string): string {
    return `${this.Actions('manage')}/clone/${taskId}`;
  }

  ActionEdit(taskId: string): string {
    return `${this.Actions('manage')}/edit/${taskId}`;
  }

  ActionCreate(formId?: number): string {
    let str = `${this.Actions('manage')}/create`;

    if (formId) {
      str += `?formId=${formId}`;
    }
    return str;
  }

  ViewActionSupervisorApproval(taskId: string | number): string {
    return `${this.Actions('supervisor-approval')}/${taskId}`;
  }

  Admin(): string {
    return `${this.LANDING}${this.ADMIN}`;
  }

  AdminSystem(): string {
    return this.Admin();
  }

  AdminAssistantControlCenter(): string {
    return `${this.Admin()}/assistant`;
  }

  AdminCloudWatch(): string {
    return `${this.Admin()}/cloudwatch`;
  }

  AdminHangfireMonitor(): string {
    return `${this.Admin()}/${this.BACKGROUND_JOBS}/hangfire`;
  }

  AdminList(): string {
    return `${this.Dashboard()}/admin`;
  }

  Analytics(): string {
    return `${this.Dashboard()}/${this.ANALYTICS}`;
  }

  Approvals(): string {
    return this.TasksDashboard(TaskTypeEnum.Approval, 'user');
  }

  Assets(): string {
    return `${this.Dashboard()}/${this.ASSETS}`;
  }

  AssetCreate(parentId?: string): string {
    if (parentId) {
      return `${this.Assets()}/create/${parentId}`;
    }

    return `${this.Assets()}/create/root`;
  }

  AssetEdit(id: string | number, view: AssetsViewType): string {
    return `${this.Assets()}/edit/${id}/${view}`;
  }

  AssetView(id: string | number, view: AssetsViewType): string {
    return `${this.Assets()}/${id}/${view}`;
  }

  AssignedForms(): string {
    return this.TasksDashboard(TaskTypeEnum.Form, 'user');
  }

  AuditLogs(): string {
    return `${this.Dashboard()}/${this.AUDIT_LOGS}`;
  }

  AuditLogsWithView(view: AuditLogsViewType): string {
    return `${this.AuditLogs()}/${view}`;
  }

  AuditLogView(id: string | number): string {
    return `${this.AuditLogsWithView('list')}/${id}`;
  }

  BulletinDashboard(view: BulletinDashViewType): string {
    return `${this.Dashboard()}/${this.BULLETIN}/${view}`;
  }
  BulletinDashboardParameterless(): string {
    return `${this.Dashboard()}/${this.BULLETIN}`;
  }

  BulletinCreate(view: BulletinViewType): string {
    return `${this.BulletinDashboard('manage')}/create/${view}`;
  }

  BulletinEdit(id: string | number, view: BulletinViewType): string {
    return `${this.BulletinDashboard('manage')}/edit/${id}/${view}`;
  }

  BulletinView(id: string | number, view: BulletinViewType): string {
    return `${this.BulletinDashboard('manage')}/${id}/${view}`;
  }

  ComplianceStandards(): string {
    return `${this.Admin()}/${this.COMPLIANCE_STANDARDS}`;
  }

  ComplianceStandardsView(id: string | number): string {
    return `${this.ComplianceStandards()}/${id}`;
  }

  Contact(): string {
    return 'https://proactehs.com/contact';
  }

  CreateAccount(token?: string, user?: string): string {
    if (token === undefined || user === undefined) {
      return `${this.LANDING}${this.CREATE_ACCOUNT}`;
    }
    return `${this.LANDING}${this.CREATE_ACCOUNT}/${token}/${user}`;
  }

  CustomComplianceStandards(): string {
    return `${this.Dashboard()}/${this.CUSTOM_COMPLIANCE_STANDARDS}`;
  }

  CustomComplianceStandardsCreate(): string {
    return `${this.CustomComplianceStandards()}/create`;
  }

  CustomComplianceStandardsEdit(id: string | number): string {
    return `${this.CustomComplianceStandards()}/edit/${id}`;
  }

  CustomComplianceStandardsView(id: string | number) {
    return `${this.CustomComplianceStandards()}/${id}`;
  }

  Dashboard(): string {
    return `${this.LANDING}${this.DASHBOARD}`;
  }

  /** Both paths that render the dashboard landing page. */
  DashboardRoots = (): string[] => [this.Landing(), this.Dashboard()];

  DashboardSection(section: string): string {
    return `${this.Dashboard()}/${section}`;
  }

  Assistant(conversationId?: string | number): string {
    const base = `${this.Dashboard()}/${this.ASSISTANT}`;
    return conversationId === undefined ? base : `${base}/${conversationId}`;
  }

  AssistantControlCenter(): string {
    return `${this.Dashboard()}/assistant-control-center`;
  }

  KnowledgeBase(): string {
    return `${this.Dashboard()}/${this.KNOWLEDGE_BASE}`;
  }

  KnowledgeBaseCreate(): string {
    return `${this.KnowledgeBase()}/create`;
  }

  KnowledgeBaseEdit(id: string | number): string {
    return `${this.KnowledgeBase()}/edit/${id}`;
  }

  KnowledgeBaseView(id: string | number): string {
    return `${this.KnowledgeBase()}/${id}`;
  }

  DocumentManagerCreateFile(parentId: string | number): string {
    return `${this.Dashboard()}/${
      this.DOCUMENTS_MANAGER
    }/create-file/${parentId}`;
  }

  DocumentManagerCreateFolder(parentId: string | number): string {
    return `${this.Dashboard()}/${
      this.DOCUMENTS_MANAGER
    }/create-folder/${parentId}`;
  }

  DocumentManagerEditFile(nodeId: string | number): string {
    return `${this.Dashboard()}/${this.DOCUMENTS_MANAGER}/edit-file/${nodeId}`;
  }

  DocumentManagerEditFolder(nodeId: string | number): string {
    return `${this.Dashboard()}/${
      this.DOCUMENTS_MANAGER
    }/edit-folder/${nodeId}`;
  }

  DocumentsManagerList(nodeId?: string | number): string {
    if (!nodeId) {
      nodeId = 0;
    }
    return `${this.Dashboard()}/${this.DOCUMENTS_MANAGER}/${nodeId}`;
  }

  DocumentManagerViewFile(
    nodeId: string | number,
    view: DocumentManagementViewType
  ): string {
    return `${this.Dashboard()}/${
      this.DOCUMENTS_MANAGER
    }/View/${nodeId}/${view}`;
  }

  ItemAssignments(definitionId: string | number): string {
    return `${this.ItemDefinitions()}/${definitionId}/${this.ITEM_ASSIGNMENT}`;
  }

  ItemAssignmentCreate(definitionId: string | number): string {
    return `${this.ItemAssignments(definitionId)}/create`;
  }

  ItemAssignmentClone(
    definitionId: string | number,
    id: string | number
  ): string {
    return `${this.ItemAssignments(definitionId)}/clone/${id}`;
  }

  ItemAssignmentEdit(
    definitionId: string | number,
    id: string | number
  ): string {
    return `${this.ItemAssignments(definitionId)}/edit/${id}`;
  }

  ItemAssignmentView(
    definitionId: string | number,
    id: string | number
  ): string {
    return `${this.ItemAssignments(definitionId)}/${id}`;
  }

  ItemDefinitions(): string {
    return `${this.Dashboard()}/${this.ITEM_DEFINITION}`;
  }

  ItemDefinitionCreate(): string {
    return `${this.ItemDefinitions()}/create`;
  }

  ItemDefinitionEdit(
    id: string | number,
    view: ItemDefinitionViewType
  ): string {
    return `${this.ItemDefinitions()}/edit/${id}/${view}`;
  }

  ItemDefinitionView(
    id: string | number,
    view: ItemDefinitionViewType
  ): string {
    return `${this.ItemDefinitions()}/${id}/${view}`;
  }

  ItemTrackerDashboard(): string {
    return `${this.Dashboard()}/${this.ITEM_TRACKER}`;
  }

  ItemTrackerRequest(): string {
    return `${this.ItemTrackerDashboard()}/request`;
  }

  ItemTrackerView(definitionId: string | number, id: string | number): string {
    return `${this.ItemTrackerDashboard()}/view/${definitionId}/${id}`;
  }

  ItemTrackerReview(
    definitionId: string | number,
    id: string | number
  ): string {
    return `${this.ItemTrackerDashboard()}/review/${definitionId}/${id}`;
  }

  ItemTrackerIssueCertificate(
    definitionId: string | number,
    id: string | number
  ): string {
    return `${this.ItemTrackerDashboard()}/certificate/${definitionId}/${id}`;
  }

  Error(): string {
    return `${this.Dashboard()}/${this.ERROR}`;
  }

  ForgotPassword(): string {
    return `${this.LANDING}${this.FORGOT_PASSWORD}`;
  }

  FormsDashboard(view: FormsDashViewType): string {
    return `${this.Dashboard()}/forms/${view}`;
  }

  FormBuilderCreate(): string {
    return `${this.FormsDashboard('manage')}/create`;
  }

  FormBuilderView(id: string | number): string {
    return `${this.FormsDashboard('manage')}/${id}`;
  }

  FormEdit(
    formDefinitionId: string | number,
    formSubmissionId: string | number
  ) {
    return `${this.FormsDashboard(
      'user'
    )}/${formDefinitionId}/edit/${formSubmissionId}`;
  }

  FormTaskEdit(
    formDefinitionId: string | number,
    taskAssignmentId: string | number
  ): string {
    return `${this.FormsDashboard(
      'user'
    )}/${formDefinitionId}/task/${taskAssignmentId}`;
  }

  FormsList(formDefinitionId: string | number): string {
    return `${this.FormsDashboard('user')}/${formDefinitionId}/list`;
  }

  FormSubmission(
    formDefinitionId: string | number,
    formSubmissionId: string | number,
    view: FormViewType
  ): string {
    return `${this.FormsDashboard(
      'user'
    )}/${formDefinitionId}/${formSubmissionId}/${view}`;
  }

  FormSubmit(formDefinitionId: string | number): string {
    return `${this.FormsDashboard('user')}/${formDefinitionId}/submit`;
  }

  GlobalList(): string {
    return `${this.Dashboard()}/system`;
  }

  InactiveUser(): string {
    return `${this.LANDING}${this.INACTIVE_USER}`;
  }

  InstanceComplianceStandards(tab?: string): string {
    const base = `${this.Dashboard()}/${this.INSTANCE_COMPLIANCE_STANDARDS}`;
    return tab ? `${base}/${tab}` : base;
  }

  InstanceComplianceStandardsCreate(id: string | number): string {
    return `${this.Dashboard()}/${
      this.INSTANCE_COMPLIANCE_STANDARDS
    }/create/${id}`;
  }

  InstanceComplianceStandardsEdit(id: string | number): string {
    return `${this.Dashboard()}/${
      this.INSTANCE_COMPLIANCE_STANDARDS
    }/edit/${id}`;
  }

  InstanceComplianceStandardsView(id: string | number): string {
    return `${this.Dashboard()}/${
      this.INSTANCE_COMPLIANCE_STANDARDS
    }/view/${id}`;
  }

  Accounts(): string {
    return `${this.Admin()}/${this.ACCOUNTS}`;
  }

  AccountsView(id: string | number): string {
    return `${this.Accounts()}/${id}`;
  }

  Instances(): string {
    return `${this.Admin()}/${this.INSTANCES}`;
  }

  SuperAdmins(): string {
    return `${this.Admin()}/${this.SUPER_ADMINS}`;
  }

  InstancesCreate(): string {
    return `${this.Instances()}/create`;
  }

  InstancesEdit(id: string | number): string {
    return `${this.Instances()}/edit/${id}`;
  }

  InstancesClone(id: string | number): string {
    return `${this.Instances()}/clone/${id}`;
  }

  InstancesDelete(id: string | number): string {
    return `${this.Instances()}/delete/${id}`;
  }

  InstancesView(
    id: string | number,
    view: 'overview' | 'administrators' | 'integrations' | 'sso' | '' | ':view'
  ): string {
    return `${this.Instances()}/${id}/${view}`;
  }

  JobHazardAnalysis(): string {
    return `${this.Dashboard()}/${this.JOB_HAZARD_ANALYSIS}`;
  }

  JobHazardAnalysisCreate(): string {
    return `${this.JobHazardAnalysis()}/create`;
  }

  JobHazardAnalysisEdit(jhAnalysisId: string): string {
    return `${this.JobHazardAnalysis()}/edit/${jhAnalysisId}`;
  }

  JobHazardAnalysisView(
    jhAnalysisId: string,
    view: JobHazardAnalysisViewType
  ): string {
    return `${this.JobHazardAnalysis()}/${jhAnalysisId}/${view}`;
  }

  Landing(): string {
    return this.LANDING;
  }

  Security(): string {
    return `${this.Dashboard()}/security`;
  }

  Login(): string {
    return `${this.LANDING}${this.LOGIN}`;
  }

  Logout(): string {
    return `${this.LANDING}${this.LOGOUT}`;
  }

  Invitation(): string {
    return `${this.LANDING}verify`;
  }

  NotificationRule(): string {
    return `${this.Dashboard()}/${this.NOTIFICATION_RULE}`;
  }

  NotificationRuleCreate(): string {
    return `${this.NotificationRule()}/create`;
  }

  NotificationRuleEdit(id: string | number): string {
    return `${this.NotificationRule()}/edit/${id}`;
  }

  NotificationRuleView(id: string | number): string {
    return `${this.NotificationRule()}/${id}`;
  }

  NotFound(): string {
    return `${this.Dashboard()}/${this.NOT_FOUND}`;
  }

  PageNotFound(): string {
    return `${this.LANDING}${this.PAGE_NOT_FOUND}`;
  }

  PermissionDenied(): string {
    return `${this.LANDING}${this.PERMISSION_DENIED}`;
  }

  Personnel(view: PersonnelIndexViewType): string {
    return `${this.Dashboard()}/${this.PERSONNEL}/${view}`;
  }

  PersonnelCreate(): string {
    return `${this.Personnel('employee')}/create`;
  }

  PersonnelEdit(id: string | number, view: PersonnelViewType): string {
    return `${this.Personnel('employee')}/edit/${id}/${view}`;
  }

  PersonnelView(id: string | number, view: PersonnelViewType) {
    return `${this.Personnel('employee')}/${id}/${view}`;
  }

  LegacyPersonnelView(id: string | number, view: PersonnelViewType) {
    return `/admin/personnel/${id}/${view}`;
  }

  Legacy2PersonnelView(id: string | number, view: PersonnelViewType) {
    return `/admin/personnel/employee/${id}/${view}`;
  }

  Permissions(view: 'admins' | '' | ':view') {
    return `${this.PERMISSIONS}/${view}`;
  }

  ResetCache(): string {
    return `${this.LANDING}reset-cache`;
  }

  ResetPassword(): string {
    return `${this.LANDING}${this.RESET_PASSWORD}`;
  }

  RiskAssessment(): string {
    return `${this.Dashboard()}/${this.RISK_ASSESSMENT}`;
  }

  Settings(): string {
    return `${this.Dashboard()}/settings`;
  }

  RiskAssessmentCreate(): string {
    return `${this.RiskAssessment()}/create`;
  }

  RiskAssessmentMissing(): string {
    return `${this.Dashboard()}/${this.RISK_ASSESSMENT_MISSING}`;
  }

  RiskAssessmentEdit(id: string | number) {
    return `${this.RiskAssessment()}/edit/${id}`;
  }

  RiskAssessmentView(id: string | number, view: RiskAssessmentCodeViewType) {
    return `${this.RiskAssessment()}/${id}/${view}`;
  }

  CourseVideoCreate(): string {
    return `${this.TrainingDashboard('course-video')}/create`;
  }

  CourseVideoEdit(id: string | number) {
    return `${this.TrainingDashboard('course-video')}/edit/${id}`;
  }

  CourseVideoView(id: string | number, view: CourseVideoViewType) {
    return `${this.TrainingDashboard('course-video')}/${id}/${view}`;
  }

  SafetyEvent(): string {
    return `${this.Dashboard()}/${this.SAFETY_EVENT}`;
  }

  SafetyEventCreate(): string {
    return `${this.SafetyEvent()}/create`;
  }

  SafetyEventEdit(id: string | number): string {
    return `${this.SafetyEvent()}/edit/${id}`;
  }

  SafetyEventView(id: string | number, view: SafetyEventViewType): string {
    return `${this.SafetyEvent()}/${id}/${view}`;
  }

  Signup(code: string): string {
    return `${this.LANDING}${this.SIGNUP}/${code}`;
  }

  SendMagicLink(): string {
    return `${this.LANDING}${this.SEND_MAGIC_LINK}`;
  }

  TasksDashboard(taskType: TaskTypeEnum, view: TasksDashViewType): string {
    const section = GetUrlSectionFromTaskType(taskType);
    return `${this.Dashboard()}/${section}/${view}`;
  }

  TaskView(
    taskType: TaskTypeEnum,
    taskId: string | number,
    view: 'content' | 'details' | 'assign' | 'notes' | 'notifications' | ':view'
  ): string {
    return `${this.TasksDashboard(taskType, 'manage')}/${taskId}/view/${view}`;
  }

  TaskClone(taskType: TaskTypeEnum, taskId: string): string {
    return `${this.TasksDashboard(taskType, 'manage')}/clone/${taskId}`;
  }

  TaskEdit(taskType: TaskTypeEnum, taskId: string): string {
    return `${this.TasksDashboard(taskType, 'manage')}/edit/${taskId}`;
  }

  TaskCreate(taskType: TaskTypeEnum, formId?: number): string {
    let str = `${this.TasksDashboard(taskType, 'manage')}/create`;

    if (formId) {
      str += `?formId=${formId}`;
    }
    return str;
  }

  ViewTaskAssignment(taskType: TaskTypeEnum, taskId: string | number): string {
    return `${this.TasksDashboard(taskType, 'user')}/${taskId}`;
  }

  ViewTaskSupervisorApproval(
    taskType: TaskTypeEnum,
    taskId: string | number
  ): string {
    return `${this.TasksDashboard(taskType, 'supervisor-approval')}/${taskId}`;
  }

  ToolboxTalk(): string {
    return this.TasksDashboard(TaskTypeEnum.ToolboxTalk, 'user');
  }

  TrainingDashboard(view: TrainingDashViewType): string {
    return `${this.Dashboard()}/training/${view}`;
  }

  TrainingCompliance(tab?: TrainingComplianceTabView): string {
    const base = `${this.Dashboard()}/training/compliance`;
    return tab ? `${base}?tab=${tab}` : base;
  }

  TrainingCourseOverview(assignmentId: string): string {
    return `${this.TrainingDashboard('user')}/overview/${assignmentId}`;
  }

  TrainingSessionTracker(assignmentId: string): string {
    return `${this.TrainingDashboard('user')}/session-tracker/${assignmentId}`;
  }

  TrainingSessionRecord(
    sessionId: string,
    view: ViewCourseSessionRecordViewType
  ): string {
    return `${this.TrainingDashboard(
      'user'
    )}/session-record/${sessionId}/${view}`;
  }

  TrainingSessionCertificate(sessionId?: string, contentId?: string): string {
    return contentId === undefined
      ? `${this.TrainingDashboard(
          'user'
        )}/session-record/${sessionId}/certificate`
      : `${this.TrainingDashboard(
          'user'
        )}/session-record/${sessionId}/certificate/${contentId}`;
  }

  TrainingCourseCreate(): string {
    return `${this.TrainingDashboard('manage')}/${this.COURSE_BUILDER}/create`;
  }

  TrainingCourseEdit(definitionId: string): string {
    return `${this.TrainingDashboard('manage')}/${
      this.COURSE_BUILDER
    }/edit/${definitionId}`;
  }

  TrainingCourseView(
    definitionId: string,
    view: CourseDefinitionViewType
  ): string {
    return `${this.TrainingDashboard('manage')}/${
      this.COURSE_BUILDER
    }/${definitionId}/view/${view}`;
  }

  TrainingCourseAssignmentView(
    definitionId: string,
    assignmentId: string,
    view: CourseAssignmentViewType
  ): string {
    return `${this.TrainingDashboard('manage')}/${
      this.COURSE_BUILDER
    }/${definitionId}/assignment/${assignmentId}/${view}`;
  }

  TrainingCourseAssignmentEdit(
    definitionId: string,
    assignmentId: string
  ): string {
    return `${this.TrainingDashboard('manage')}/${
      this.COURSE_BUILDER
    }/${definitionId}/assignment/${assignmentId}/edit`;
  }

  TrainingCourseClassroomCreate(definitionId: string): string {
    return `${this.TrainingDashboard('manage')}/${
      this.COURSE_BUILDER
    }/${definitionId}/classroom/create`;
  }

  TrainingCourseClassroomView(
    definitionId: string,
    classroomId: string,
    view: CourseClassroomViewType
  ): string {
    return `${this.TrainingDashboard('manage')}/${
      this.COURSE_BUILDER
    }/${definitionId}/classroom/${classroomId}/${view}`;
  }

  TrainingCourseClassroomEdit(
    definitionId: string,
    classroomId: string
  ): string {
    return `${this.TrainingDashboard('manage')}/${
      this.COURSE_BUILDER
    }/${definitionId}/classroom/${classroomId}/edit`;
  }

  TrainingCourseClassroomRun(
    definitionId: string,
    classroomId: string
  ): string {
    return `${this.TrainingDashboard('manage')}/${
      this.COURSE_BUILDER
    }/${definitionId}/classroom/${classroomId}/run`;
  }

  TrainingSchedule(view?: TrainingScheduleViewMode): string {
    const base = `${this.Dashboard()}/training/schedule`;
    return view ? `${base}?view=${view}` : base;
  }

  TrainingSignOffs(tab?: SignOffTabView): string {
    const base = `${this.Dashboard()}/training/sign-offs`;
    return tab ? `${base}?tab=${tab}` : base;
  }

  TrainingSignOffEvaluate(signOffId: string): string {
    return `${this.TrainingSignOffs()}/${signOffId}/evaluate`;
  }

  TrainingSignOffDetail(signOffId: string): string {
    return `${this.TrainingSignOffs()}/${signOffId}`;
  }

  TrainingCourseSessionView(
    definitionId: string,
    assignmentId: string,
    sessionId: string,
    view: ViewCourseSessionRecordViewType
  ): string {
    return `${this.TrainingDashboard('manage')}/${
      this.COURSE_BUILDER
    }/${definitionId}/assignment/${assignmentId}/session/${sessionId}/${view}`;
  }

  TrainingCourseSessionEdit(
    definitionId: string,
    assignmentId: string,
    sessionId: string
  ): string {
    return `${this.TrainingDashboard('manage')}/${
      this.COURSE_BUILDER
    }/${definitionId}/assignment/${assignmentId}/session/${sessionId}/edit`;
  }

  TrainingCourseSessionCertificate(
    definitionId: string,
    assignmentId: string,
    sessionId: string,
    contentId?: string
  ): string {
    const base = `${this.TrainingDashboard('manage')}/${
      this.COURSE_BUILDER
    }/${definitionId}/assignment/${assignmentId}/session/${sessionId}/certificate`;
    return contentId === undefined ? base : `${base}/${contentId}`;
  }

  UnderConstruction(): string {
    return `${this.Dashboard()}/${this.UNDER_CONSTRUCTION}`;
  }

  UserGroup(): string {
    return `${this.Dashboard()}/${this.USER_GROUP}`;
  }

  UserGroupCreate(view: UserGroupViewType): string {
    return `${this.UserGroup()}/create/${view}`;
  }

  UserGroupEdit(id: string | number, view: UserGroupViewType): string {
    return `${this.UserGroup()}/edit/${id}/${view}`;
  }

  UserGroupView(id: string | number, view: UserGroupViewType): string {
    return `${this.UserGroup()}/${id}/${view}`;
  }

  ModuleCustomization(): string {
    return `${this.Dashboard()}/${this.MODULE_CUSTOMIZATION}`;
  }

  Osha(): string {
    return `${this.Dashboard()}/${this.OSHA}`;
  }

  ModuleCustomizationDetail(module: string): string {
    return `${this.ModuleCustomization()}/${module}`;
  }

  AttachmentClassification(): string {
    return `${this.Dashboard()}/${this.ATTACHMENT_CLASSIFICATION}`;
  }

  AttachmentClassificationCreate(): string {
    return `${this.AttachmentClassification()}/create`;
  }

  AttachmentClassificationEdit(id: string | number): string {
    return `${this.AttachmentClassification()}/edit/${id}`;
  }

  AttachmentClassificationView(id: string | number): string {
    return `${this.AttachmentClassification()}/${id}`;
  }

  Project(view: ProjectIndexViewType): string {
    return `${this.Dashboard()}/${this.PROJECT}/${view}`;
  }

  ProjectCreate(): string {
    return `${this.Project('list')}/create`;
  }

  ProjectEdit(id: string | number): string {
    return `${this.Project('list')}/edit/${id}`;
  }

  ProjectView(id: string | number, view?: string): string {
    return view
      ? `${this.Project('list')}/${id}/${view}`
      : `${this.Project('list')}/${id}`;
  }

  ProjectRegionsCreate(): string {
    return `${this.Project('regions')}/create`;
  }

  ProjectRegionsEdit(id: string | number): string {
    return `${this.Project('regions')}/edit/${id}`;
  }

  Clients(view: ClientIndexViewType): string {
    return `${this.Dashboard()}/${this.CLIENTS}/${view}`;
  }

  ClientsCreate(): string {
    return `${this.Clients('list')}/create`;
  }

  ClientsEdit(id: string | number): string {
    return `${this.Clients('list')}/edit/${id}`;
  }

  ClientsView(id: string | number): string {
    return `${this.Clients('list')}/${id}`;
  }

  SitesCreate(): string {
    return `${this.Clients('sites')}/create`;
  }

  SitesEdit(id: string | number): string {
    return `${this.Clients('sites')}/edit/${id}`;
  }

  SitesView(id: string | number): string {
    return `${this.Clients('sites')}/${id}`;
  }

  Inspections(view: InspectionIndexViewType): string {
    return `${this.Dashboard()}/${this.INSPECTIONS}/${view}`;
  }

  // Static path; ranks above the `:view` list route in React Router matching.
  InspectionsDispatch(): string {
    return `${this.Dashboard()}/${this.INSPECTIONS}/dispatch`;
  }

  DevicesCreate(): string {
    return `${this.Inspections('devices')}/create`;
  }

  DevicesEdit(id: string | number): string {
    return `${this.Inspections('devices')}/edit/${id}`;
  }

  DevicesView(id: string | number): string {
    return `${this.Inspections('devices')}/${id}`;
  }

  EquipmentTypesCreate(): string {
    return `${this.Inspections('equipment-types')}/create`;
  }

  EquipmentTypesEdit(
    id: string | number,
    view?: 'details' | 'fields' | ':view' | ':view?'
  ): string {
    const base = `${this.Inspections('equipment-types')}/edit/${id}`;
    return view ? `${base}/${view}` : base;
  }

  EquipmentTypesView(
    id: string | number,
    view?: 'details' | 'fields' | ':view' | ':view?'
  ): string {
    const base = `${this.Inspections('equipment-types')}/${id}`;
    return view ? `${base}/${view}` : base;
  }

  InspectionTypesCreate(): string {
    return `${this.Inspections('inspection-types')}/create`;
  }

  InspectionTypesEdit(id: string | number): string {
    return `${this.Inspections('inspection-types')}/edit/${id}`;
  }

  InspectionTypesView(id: string | number): string {
    return `${this.Inspections('inspection-types')}/${id}`;
  }

  InspectionsCreate(): string {
    return `${this.Inspections('list')}/create`;
  }

  InspectionsEdit(id: string | number): string {
    return `${this.Inspections('list')}/${id}/edit`;
  }

  InspectionsView(id: string | number): string {
    return `${this.Inspections('list')}/${id}`;
  }

  // Guided inspector "do my inspection" route — built directly since InspectionIndexViewType
  // (list-page tabs) has no 'my' member and must not gain one.
  InspectionsMy(id: string | number): string {
    return `${this.Dashboard()}/${this.INSPECTIONS}/my/${id}`;
  }

  // Deep link to a device's entry panel.
  InspectionsRecord(id: string | number, deviceId: string | number): string {
    return `${this.InspectionsView(id)}/device/${deviceId}`;
  }

  // Anchor for deep links to the DeviceView History section.
  DevicesViewHistory(id: string | number): string {
    return `${this.DevicesView(id)}?tab=history`;
  }

  Vendors(view: VendorIndexViewType): string {
    return `${this.Dashboard()}/${this.VENDORS}/${view}`;
  }

  VendorsCreate(): string {
    return `${this.Vendors('list')}/create`;
  }

  VendorsEdit(id: string | number): string {
    return `${this.Vendors('list')}/edit/${id}`;
  }

  VendorsView(
    id: string | number,
    view:
      | 'overview'
      | 'personnel'
      | 'signupurl'
      | 'notes'
      | 'attachments'
      | ''
      | ':view'
  ): string {
    return `${this.Vendors('list')}/${id}/${view}`;
  }

  VendorPersonnelCreate(vendorId: string | number): string {
    return `${this.Vendors('personnel')}/${vendorId}/${this.PERSONNEL}/create`;
  }

  VendorPersonnelEdit(
    id: string | number,
    vendorId: string | number,
    view: PersonnelViewType
  ): string {
    return `${this.Vendors('personnel')}/${vendorId}/${
      this.PERSONNEL
    }/edit/${id}/${view}`;
  }

  VendorPersonnelView(
    id: string | number,
    vendorId: string | number,
    view: PersonnelViewType
  ) {
    return `${this.Vendors('personnel')}/${vendorId}/${
      this.PERSONNEL
    }/${id}/${view}`;
  }

  VendorListPersonnelCreate(): string {
    return `${this.Vendors('personnel')}/create`;
  }

  VendorListPersonnelEdit(
    id: string | number,
    view: PersonnelViewType
  ): string {
    return `${this.Vendors('personnel')}/edit/${id}/${view}`;
  }

  VendorListPersonnelView(id: string | number, view: PersonnelViewType) {
    return `${this.Vendors('personnel')}/${id}/${view}`;
  }

  VendorPendingPersonnelEdit(
    id: string | number,
    vendorId: string | number,
    view: PersonnelViewType
  ): string {
    return `${this.Vendors('pending')}/${vendorId}/${
      this.PERSONNEL
    }/edit/${id}/${view}`;
  }

  VendorPendingPersonnelView(
    id: string | number,
    vendorId: string | number,
    view: PersonnelViewType
  ) {
    return `${this.Vendors('pending')}/${vendorId}/${
      this.PERSONNEL
    }/${id}/${view}`;
  }

  VendorsSignup(code?: string): string {
    return `${this.Landing()}vendor-signup/${code}`;
  }

  Workflow(): string {
    return `${this.Dashboard()}/${this.WORKFLOW}`;
  }

  WorkflowsDashboard(view: string): string {
    return `${this.Workflow()}/${view}`;
  }

  WorkflowCreate(): string {
    return `${this.Workflow()}/create`;
  }

  WorkflowEdit(id: string): string {
    return `${this.Workflow()}/edit/${id}`;
  }

  WorkflowView(definitionId: string, id: string): string {
    return `${this.Workflow()}/definition/${definitionId}/${id}`;
  }

  WorkflowViewDefinition(id: string): string {
    return `${this.Workflow()}/definition/${id}`;
  }

  AdminTestArea(): string {
    return `${this.Admin()}/tests`;
  }

  AdminTestApiInteractions(): string {
    return `${this.AdminTestArea()}/api-interactions`;
  }

  AdminEditTestForm(): string {
    return `${this.AdminTestArea()}/edit-test-form`;
  }

  AdminViewTestForm(): string {
    return `${this.AdminTestArea()}/view-test-form`;
  }

  AdminOfflineTest(): string {
    return `${this.AdminTestArea()}/offline-test`;
  }

  ValidateMagicLink(): string {
    return `${this.LANDING}login/magic`;
  }

  Support(): string {
    return `${this.LANDING}support`;
  }

  LinkRedirect(): string {
    return `/link`;
  }
}

const PathUtility = new PathBuilder();

export {PathUtility};
